import { Grid } from '@mui/material';
import { dateFormatLongDate } from '@nx-smartmonkey/shared/helpers';
import { RoutalLoader, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { exportToCSV } from '../../../helpers/CSV';
import { intl } from '../../../helpers/IntGlobalProvider';
import { useRetrieveAnalyticsAllStops } from '../../../hooks/analytics/planner/useRetrieveAnalyticsAllStops';
import { useAnalyticsFilters } from '../../../hooks/analytics/useAnalyticsFilters';
import { useRetrieveOrganization } from '../../../hooks/organization/useRetrieveOrganization';
import { useRetrieveActiveProject } from '../../../hooks/project/useRetrieveActiveProject';
import { BinTypes } from '../../../interfaces/analytics';
import Button from '../../../react-components/Button';
import Text from '../../../react-components/Text';
import AnalyticsHeader from '../shared/AnalyticsHeader';
import { AnalyticsPermissions, hasAnalyticsPrivileges } from '../shared/AnalyticsPermissions';
import AnalyticsVehiclesFilter from '../shared/AnalyticsVehiclesFilter';
import ChartCustomTooltip from '../shared/ChartCustomTooltip';
import ChartHeaderFilters from '../shared/ChartHeaderFilters';
import AnalyticsTable, { AlignmentTypes } from '../shared/Table';

export const AllStopsChartCustomTooltip = ({ active, payload, label, ...other }: any) => {
  if (!active || (payload ?? []).length === 0) {
    return null;
  }

  return (
    <ChartCustomTooltip
      label={payload[0].payload.dateLabel}
      data={payload.map(({ color, name, value }: any) => ({
        label: intl.formatMessage({ id: `project_analytics.planner.all_stops.stop_status.${name}` }),
        value,
        color,
      }))}
      previousChange={payload[0].payload.previousChange}
    />
  );
};

export interface AllStopsDataProps {
  date: string;
  dateLabel: string;
  pending: number;
  incomplete: number;
  completed: number;
  canceled: number;
  total: number;
  previousChange?: number;
}

const ProjectAnalyticsPlannerAllStops = () => {
  const { data: organization } = useRetrieveOrganization();
  const { data: activeProject } = useRetrieveActiveProject();
  const { binType, chartType, stackedChart, cumulativeChart, startDate, endDate, vehicles } = useAnalyticsFilters({
    project: activeProject,
  });
  const { data, isLoading } = useRetrieveAnalyticsAllStops({
    startDate,
    endDate,
    binType: (binType ?? `day`) as BinTypes,
    vehicleId: vehicles.length > 0 ? vehicles[0].id : undefined,
  });

  if (
    !hasAnalyticsPrivileges({
      planType: organization?.subscription.plan,
    })
  ) {
    return <AnalyticsPermissions />;
  }

  const getData = () => {
    return cumulativeChart
      ? data.reduce((acc, curr) => {
          acc.push({
            date: curr.date,
            dateLabel: curr.dateLabel,
            pending: (acc[acc.length - 1]?.pending ?? 0) + curr.pending,
            incomplete: (acc[acc.length - 1]?.incomplete ?? 0) + curr.incomplete,
            completed: (acc[acc.length - 1]?.completed ?? 0) + curr.completed,
            canceled: (acc[acc.length - 1]?.canceled ?? 0) + curr.canceled,
            total: (acc[acc.length - 1]?.total ?? 0) + curr.total,
            previousChange: (acc[acc.length - 1]?.previousChange ?? 0) + (curr.previousChange ?? 0),
          });
          return acc;
        }, [] as AllStopsDataProps[])
      : data;
  };

  const getChart = () => {
    let content;
    switch (chartType) {
      case `line`:
        if (stackedChart) {
          content = (
            <AreaChart
              data={getData()}
              margin={{
                top: 0,
                right: 40,
                left: 0,
                bottom: 0,
              }}
            >
              <CartesianGrid vertical={false} />
              <XAxis dataKey="dateLabel" fontSize="13px" />
              <YAxis allowDecimals={false} fontSize="13px" />
              <Tooltip content={<AllStopsChartCustomTooltip />} />
              <Area
                type="linear"
                dataKey="pending"
                stackId="1"
                stroke={RoutalPalette.statusColors.pending}
                strokeWidth={2}
                fill={RoutalPalette.statusColors.pending}
                dot={{ r: 3, fillOpacity: 1, stroke: `none`, strokeWidth: 0, fill: RoutalPalette.statusColors.pending }}
                activeDot={{ r: 3, stroke: RoutalPalette.statusColors.pending, strokeWidth: 2, fill: `#FFF` }}
              />
              <Area
                type="linear"
                dataKey="completed"
                stackId="1"
                stroke={RoutalPalette.statusColors.completed}
                strokeWidth={2}
                fill={RoutalPalette.statusColors.completed}
                dot={{
                  r: 3,
                  fillOpacity: 1,
                  stroke: `none`,
                  strokeWidth: 0,
                  fill: RoutalPalette.statusColors.completed,
                }}
                activeDot={{ r: 3, stroke: RoutalPalette.statusColors.completed, strokeWidth: 2, fill: `#FFF` }}
              />
              <Area
                type="linear"
                dataKey="incomplete"
                stackId="1"
                stroke={RoutalPalette.statusColors.incomplete}
                strokeWidth={2}
                fill={RoutalPalette.statusColors.incomplete}
                dot={{
                  r: 3,
                  fillOpacity: 1,
                  stroke: `none`,
                  strokeWidth: 0,
                  fill: RoutalPalette.statusColors.incomplete,
                }}
                activeDot={{ r: 3, stroke: RoutalPalette.statusColors.incomplete, strokeWidth: 2, fill: `#FFF` }}
              />
              <Area
                type="linear"
                dataKey="canceled"
                stackId="1"
                stroke={RoutalPalette.statusColors.canceled}
                strokeWidth={2}
                fill={RoutalPalette.statusColors.canceled}
                dot={{
                  r: 3,
                  fillOpacity: 1,
                  stroke: `none`,
                  strokeWidth: 0,
                  fill: RoutalPalette.statusColors.canceled,
                }}
                activeDot={{ r: 3, stroke: RoutalPalette.statusColors.canceled, strokeWidth: 2, fill: `#FFF` }}
              />
            </AreaChart>
          );
          break;
        }

        content = (
          <LineChart
            data={getData()}
            margin={{
              top: 0,
              right: 40,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid vertical={false} />
            <XAxis dataKey="dateLabel" fontSize="13px" />
            <YAxis allowDecimals={false} fontSize="13px" />
            <Tooltip content={<AllStopsChartCustomTooltip />} />
            <Line
              type="linear"
              dataKey="pending"
              stroke={RoutalPalette.statusColors.pending}
              strokeWidth={2}
              dot={{ r: 3, fillOpacity: 1, stroke: `none`, strokeWidth: 0, fill: RoutalPalette.statusColors.pending }}
              activeDot={{ r: 3, stroke: RoutalPalette.statusColors.pending, strokeWidth: 2, fill: `#FFF` }}
            />
            <Line
              type="linear"
              dataKey="completed"
              stroke={RoutalPalette.statusColors.completed}
              strokeWidth={2}
              dot={{ r: 3, fillOpacity: 1, stroke: `none`, strokeWidth: 0, fill: RoutalPalette.statusColors.completed }}
              activeDot={{ r: 3, stroke: RoutalPalette.statusColors.completed, strokeWidth: 2, fill: `#FFF` }}
            />
            <Line
              type="linear"
              dataKey="incomplete"
              stroke={RoutalPalette.statusColors.incomplete}
              strokeWidth={2}
              dot={{
                r: 3,
                fillOpacity: 1,
                stroke: `none`,
                strokeWidth: 0,
                fill: RoutalPalette.statusColors.incomplete,
              }}
              activeDot={{ r: 3, stroke: RoutalPalette.statusColors.incomplete, strokeWidth: 2, fill: `#FFF` }}
            />
            <Line
              type="linear"
              dataKey="canceled"
              stroke={RoutalPalette.statusColors.canceled}
              strokeWidth={2}
              dot={{ r: 3, fillOpacity: 1, stroke: `none`, strokeWidth: 0, fill: RoutalPalette.statusColors.canceled }}
              activeDot={{ r: 3, stroke: RoutalPalette.statusColors.canceled, strokeWidth: 2, fill: `#FFF` }}
            />
          </LineChart>
        );
        break;
      case `bar`:
        if (stackedChart) {
          content = (
            <BarChart
              data={getData()}
              margin={{
                top: 0,
                right: 40,
                left: 0,
                bottom: 0,
              }}
            >
              <CartesianGrid vertical={false} />
              <XAxis dataKey="dateLabel" fontSize="13px" />
              <YAxis allowDecimals={false} fontSize="13px" />
              <Tooltip content={<AllStopsChartCustomTooltip />} />
              <Bar barSize={80} dataKey="pending" stackId="a" fill={RoutalPalette.statusColors.pending} />
              <Bar barSize={80} dataKey="completed" stackId="a" fill={RoutalPalette.statusColors.completed} />
              <Bar barSize={80} dataKey="incomplete" stackId="a" fill={RoutalPalette.statusColors.incomplete} />
              <Bar barSize={80} dataKey="canceled" stackId="a" fill={RoutalPalette.statusColors.canceled} />
            </BarChart>
          );
          break;
        }
        content = (
          <BarChart
            data={getData()}
            margin={{
              top: 0,
              right: 40,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid vertical={false} />
            <XAxis dataKey="dateLabel" fontSize="13px" />
            <YAxis allowDecimals={false} fontSize="13px" />
            <Tooltip content={<AllStopsChartCustomTooltip />} />
            <Bar barSize={20} dataKey="pending" fill={RoutalPalette.statusColors.pending} />
            <Bar barSize={20} dataKey="completed" fill={RoutalPalette.statusColors.completed} />
            <Bar barSize={20} dataKey="incomplete" fill={RoutalPalette.statusColors.incomplete} />
            <Bar barSize={20} dataKey="canceled" fill={RoutalPalette.statusColors.canceled} />
          </BarChart>
        );
        break;
      default:
        return null;
    }

    return (
      <div style={{ width: `100%`, height: 400 }}>
        {isLoading ? (
          <Grid container alignItems="center" justifyContent="center" style={{ height: `100%` }}>
            <RoutalLoader width={60} />
          </Grid>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {content}
          </ResponsiveContainer>
        )}
      </div>
    );
  };

  const getTableData = () => {
    const tableData = [
      {
        label: intl.formatMessage({ id: `project_analytics.planner.all_stops.stop_status.pending` }),
      },
      {
        label: intl.formatMessage({ id: `project_analytics.planner.all_stops.stop_status.completed` }),
      },
      {
        label: intl.formatMessage({ id: `project_analytics.planner.all_stops.stop_status.incomplete` }),
      },
      {
        label: intl.formatMessage({ id: `project_analytics.planner.all_stops.stop_status.canceled` }),
      },
      {
        label: intl.formatMessage({ id: `project_analytics.totals` }),
      },
      {
        label: intl.formatMessage({ id: `project_analytics.previous_period_change` }),
      },
    ] as Record<string, any>[];

    const tableColumns = [
      {
        column: `label`,
        align: `center` as AlignmentTypes,
        width: 180,
        name: <Text tagName="span" intlId="project_analytics.planner.all_stops.stop_status" />,
        formatter: (label: string) => <Text>{label}</Text>,
      },
    ] as Record<string, any>[];

    data.forEach((d, index) => {
      tableColumns.push({
        column: d.dateLabel,
        align: `center` as AlignmentTypes,
        width: 135,
        name: <Text tagName="span">{d.dateLabel}</Text>,
        formatter: (value: number | string) => <Text>{value ?? `-`}</Text>,
      });
      tableData[0][d.dateLabel] = d.pending;
      tableData[1][d.dateLabel] = d.completed;
      tableData[2][d.dateLabel] = d.incomplete;
      tableData[3][d.dateLabel] = d.canceled;

      const total = d.pending + d.completed + d.incomplete + d.canceled;
      tableData[4][d.dateLabel] = total;

      // Calculates the previous period change
      if (index > 0) {
        const previousTotal =
          data[index - 1].pending + data[index - 1].completed + data[index - 1].incomplete + data[index - 1].canceled;

        const difference = total - previousTotal;

        let change = 0;

        if (difference !== 0) {
          change = previousTotal > 0 ? Math.floor((difference / previousTotal) * 100) : 100;
        }

        tableData[5][d.dateLabel] = `${change}%`;
      }
    });

    return { tableData, tableColumns };
  };

  const downloadCSV = () => {
    const { tableData, tableColumns } = getTableData();

    const firstColumnHeader = intl.formatMessage({ id: `project_analytics.planner.all_stops.stop_status` });

    const headers = [firstColumnHeader, ...tableColumns.slice(1, tableColumns.length).map((c) => c.column)];

    const data = tableData.map((d) => {
      return {
        [firstColumnHeader]: d.label,
        ...d,
        label: undefined,
      };
    });

    exportToCSV({
      filename: `${intl.formatMessage({ id: `project_analytics.planner.all_stops.title` })}_${dateFormatLongDate(
        new Date()
      )}`,
      data,
      headers,
    });
  };

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      style={{ width: `100%`, height: ` 100%`, backgroundColor: `#F6F6F6` }}
    >
      <AnalyticsHeader titleIntlId="project_analytics.planner.all_stops.title" />

      <Grid container>
        <AnalyticsVehiclesFilter isSingleVehicle />
      </Grid>

      <Grid
        container
        direction="column"
        wrap="nowrap"
        style={{
          padding: `16px 22px`,
          backgroundColor: `#F6F6F6`,
        }}
      >
        <Grid item container>
          <Grid item style={{ border: `1px solid #E0E0E0`, width: `100%`, backgroundColor: `white` }}>
            <ChartHeaderFilters />

            <VSpacer medium />

            <Grid
              container
              style={{
                padding: `20px 24px`,
              }}
            >
              {getChart()}
            </Grid>
          </Grid>
        </Grid>

        <VSpacer large />

        <Grid
          item
          container
          style={{
            width: `100%`,
            backgroundColor: `white`,
          }}
        >
          <Grid
            item
            container
            direction="row"
            wrap="nowrap"
            justifyContent="space-between"
            alignItems="center"
            style={{
              borderTop: `1px solid #E0E0E0`,
              borderLeft: `1px solid #E0E0E0`,
              borderRight: `1px solid #E0E0E0`,
              width: `100%`,
              padding: `16px 21px`,
            }}
          >
            <Text variant="h6" isBold intlId="project_analytics.chart_information" />

            <Button
              disabled={isLoading}
              onClick={() => downloadCSV()}
              intlId="project_analytics.export_csv"
              variant="flat"
            />
          </Grid>

          {isLoading ? (
            <Grid
              container
              alignItems="center"
              justifyContent="center"
              style={{
                height: `400px`,
                borderBottom: `1px solid #E0E0E0`,
                borderLeft: `1px solid #E0E0E0`,
                borderRight: `1px solid #E0E0E0`,
              }}
            >
              <RoutalLoader width={60} />
            </Grid>
          ) : (
            <AnalyticsTable docs={getTableData().tableData} mapping={getTableData().tableColumns} />
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ProjectAnalyticsPlannerAllStops;
